import { z } from "zod";

export const localeSchema = z.enum(["sv", "en"]);
export type Locale = z.infer<typeof localeSchema>;

// How every clinician-facing LIST row identifies its patient: the BankID name
// (null for pre-name-capture / dev-auth / anonymised rows) plus the last six
// personnummer digits the doctors use as shorthand. Shared so the inbox, the
// work queue, the review queue, the chat-draft queue and the meeting list all
// identify the same person the same way — the fields are `.optional()` on each
// DTO that mixes them in, so an older API response (or a queue whose enrichment
// has not landed yet) degrades to the previous rendering instead of failing to
// parse.
export const patientLabelSchema = z.object({
  patientName: z.string().nullable(),
  personalNumberLast6: z.string(),
});
export type PatientLabel = z.infer<typeof patientLabelSchema>;

export const flagSeveritySchema = z.enum(["low", "medium", "high", "urgent"]);
export type FlagSeverity = z.infer<typeof flagSeveritySchema>;

export const flagSchema = z.object({
  rule: z.string(),
  severity: flagSeveritySchema,
  escalate: z.enum(["none", "doctor", "urgent"]).default("none"),
  detail: z.string().optional(),
  // TES-948 — the answer keys the rule read, primary question first and any
  // free-text follow-up after it. The doctor's flag list resolves them to the
  // patient's actual answer and the question label. Optional: flags stored
  // before TES-948, and rules without a single source question, carry none.
  fieldKeys: z.array(z.string()).optional(),
  // TES-1001 part 1 — set when the flag comes from the versioned risk-code
  // list (risk-codes.v1.ts): its code, the list version, the priority the
  // list gives it (null = not stated in that version) and where it goes.
  // Absent on every flag raised by a form evaluator.
  riskCode: z.string().optional(),
  riskListVersion: z.string().optional(),
  riskPriority: z.enum(["high", "elevated", "normal"]).nullable().optional(),
  riskDestination: z.string().optional(),
});
export type Flag = z.infer<typeof flagSchema>;

export const hardStopSchema = z.object({
  rule: z.string(),
  detail: z.string().optional(),
  patientMessageKey: z.string().optional(),
  urgent: z.boolean().optional(),
  // TES-948 — as on flagSchema.
  fieldKeys: z.array(z.string()).optional(),
});
export type HardStop = z.infer<typeof hardStopSchema>;
